import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import {
  getQuoteRequestLineQueryData,
  quoteRequestLineQuery,
  setQuoteRequestLineQueryData,
} from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { updateQuoteRequestLineMargins } from "#modules/quoteRequestLines/infra/updateQuoteRequestLineMargins.api";

export const useUpdateQuoteRequestLineMargins = () => {
  const queryClient = useQueryClient();

  const { mutate, ...rest } = useMutation({
    mutationFn: updateQuoteRequestLineMargins,
    onMutate: async (data) => {
      await queryClient.cancelQueries({
        queryKey: quoteRequestLineQuery({ id: data.id }).queryKey,
      });

      const previousData = getQuoteRequestLineQueryData(queryClient, data.id);
      setQuoteRequestLineQueryData(queryClient, data.id, (oldData) =>
        oldData
          ? {
              ...oldData,
              materialMarginRatio:
                data.payload.materialMarginRatio ?? oldData.materialMarginRatio ?? 0,
              addedValueMarginRatio:
                data.payload.addedValueMarginRatio ?? oldData.addedValueMarginRatio ?? 0,
              productsMarginRatio:
                data.payload.productsMarginRatio ?? oldData.productsMarginRatio ?? 0,
            }
          : undefined,
      );

      return { previousData };
    },
    onError: (err: Error, data, context) => {
      setQuoteRequestLineQueryData(queryClient, data.id, context?.previousData);

      showErrorToast({ title: err.message });
      errorHandler(err);
    },
    onSettled: (data) => {
      if (!data?.id) {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: quoteRequestLineQuery({ id: data.id }).queryKey,
      });
    },
  });

  return { updateQuoteRequestLineMargins: mutate, ...rest };
};
